import React, { useEffect, useState } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, BarChart, Bar } from 'recharts';
import { supabase } from '../lib/supabase';
import { Language } from '../types';

interface ProgressProps {
  language: Language;
}

interface ProgressData {
  created_at: string;
  questions_answered: number;
  correct_answers: number;
  operation: string;
  mode: string;
}

const Progress: React.FC<ProgressProps> = ({ language }) => {
  const [progressData, setProgressData] = useState<ProgressData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchProgressData();
  }, []);

  const fetchProgressData = async () => {
    try {
      const { data, error } = await supabase
        .from('user_progress')
        .select('*')
        .order('created_at', { ascending: true });

      if (error) throw error;
      setProgressData(data || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-slate-600 dark:text-slate-300">
          {language === 'en' ? 'Loading...' : 'Chargement...'}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-red-600 dark:text-red-400 text-center p-4">
        {language === 'en' ? 'Error loading progress data' : 'Erreur lors du chargement des données'}
      </div>
    );
  }

  if (progressData.length === 0) {
    return (
      <div className="text-center p-8">
        <h2 className="text-2xl font-semibold text-slate-800 dark:text-slate-100 mb-4">
          {language === 'en' ? 'No Progress Data Yet' : 'Pas encore de données de progression'}
        </h2>
        <p className="text-slate-600 dark:text-slate-300">
          {language === 'en' 
            ? 'Complete some exercises to see your progress here!'
            : 'Complétez des exercices pour voir votre progression ici !'}
        </p>
      </div>
    );
  }

  // Calculate global statistics
  const globalStats = progressData.reduce((acc, entry) => {
    acc.totalExercises += entry.questions_answered;
    acc.correctAnswers += entry.correct_answers;
    return acc;
  }, { totalExercises: 0, correctAnswers: 0 });

  const globalAccuracy = Math.round((globalStats.correctAnswers / globalStats.totalExercises) * 100);

  // Group data by operation
  const groupedData = progressData.reduce((acc, entry) => {
    const date = new Date(entry.created_at).toLocaleDateString();
    if (!acc[entry.operation]) {
      acc[entry.operation] = {};
    }
    if (!acc[entry.operation][date]) {
      acc[entry.operation][date] = {
        correct: 0,
        total: 0,
      };
    }
    acc[entry.operation][date].correct += entry.correct_answers;
    acc[entry.operation][date].total += entry.questions_answered;
    return acc;
  }, {} as Record<string, Record<string, { correct: number; total: number }>>);

  // Calculate statistics by operation
  const operationStats = Object.entries(groupedData).reduce((acc, [operation, data]) => {
    const totalCorrect = Object.values(data).reduce((sum, d) => sum + d.correct, 0);
    const totalQuestions = Object.values(data).reduce((sum, d) => sum + d.total, 0);
    acc[operation] = {
      accuracy: Math.round((totalCorrect / totalQuestions) * 100),
      total: totalQuestions
    };
    return acc;
  }, {} as Record<string, { accuracy: number; total: number }>);

  // Convert grouped data to chart format
  const getChartData = (operation: string) => {
    return Object.entries(groupedData[operation] || {}).map(([date, data]) => ({
      date,
      accuracy: Math.round((data.correct / data.total) * 100),
    }));
  };

  const operationTitles = {
    multiplication: language === 'en' ? 'Multiplication Tables' : 'Tables de Multiplication',
    division: language === 'en' ? 'Division Tables' : 'Tables de Division',
    prime: language === 'en' ? 'Prime Numbers' : 'Nombres Premiers',
    square: language === 'en' ? 'Square Numbers' : 'Nombres Carrés',
  };

  const operationColors = {
    multiplication: '#4f46e5',
    division: '#0891b2',
    prime: '#059669',
    square: '#9333ea',
  };

  return (
    <div className="w-full max-w-4xl mx-auto p-6">
      <h1 className="text-3xl font-bold text-indigo-700 dark:text-indigo-400 mb-8 text-center">
        {language === 'en' ? 'Your Progress' : 'Votre Progression'}
      </h1>

      {/* Global Progress Overview */}
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-md p-6 mb-8 border border-slate-200 dark:border-slate-700">
        <h2 className="text-xl font-semibold text-slate-800 dark:text-slate-100 mb-4">
          {language === 'en' ? 'Global Progress' : 'Progression Globale'}
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="bg-slate-50 dark:bg-slate-700 p-4 rounded-lg text-center">
            <div className="text-3xl font-bold text-indigo-700 dark:text-indigo-400">
              {globalStats.totalExercises}
            </div>
            <div className="text-sm text-slate-600 dark:text-slate-300">
              {language === 'en' ? 'Total Exercises' : 'Exercices Totaux'}
            </div>
          </div>
          <div className="bg-slate-50 dark:bg-slate-700 p-4 rounded-lg text-center">
            <div className="text-3xl font-bold text-indigo-700 dark:text-indigo-400">
              {globalStats.correctAnswers}
            </div>
            <div className="text-sm text-slate-600 dark:text-slate-300">
              {language === 'en' ? 'Correct Answers' : 'Réponses Correctes'}
            </div>
          </div>
          <div className="bg-slate-50 dark:bg-slate-700 p-4 rounded-lg text-center">
            <div className="text-3xl font-bold text-indigo-700 dark:text-indigo-400">
              {globalAccuracy}%
            </div>
            <div className="text-sm text-slate-600 dark:text-slate-300">
              {language === 'en' ? 'Global Accuracy' : 'Précision Globale'}
            </div>
          </div>
        </div>

        <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-4">
          {Object.entries(operationTitles).map(([operation, title]) => (
            <div key={operation} className="text-center">
              <div className="text-sm font-medium text-slate-600 dark:text-slate-300 mb-1">
                {title}
              </div>
              <div className="text-lg font-semibold text-indigo-600 dark:text-indigo-400">
                {operationStats[operation]?.accuracy || 0}%
              </div>
              <div className="text-xs text-slate-500 dark:text-slate-400">
                ({operationStats[operation]?.total || 0} {language === 'en' ? 'exercises' : 'exercices'})
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Category Charts */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {Object.entries(operationTitles).map(([operation, title]) => (
          <div key={operation} className="bg-white dark:bg-slate-800 rounded-xl shadow-md p-6 border border-slate-200 dark:border-slate-700">
            <h2 className="text-xl font-semibold text-slate-800 dark:text-slate-100 mb-4">
              {title}
            </h2>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={getChartData(operation)}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                  <XAxis dataKey="date" stroke="#94a3b8" />
                  <YAxis domain={[0, 100]} stroke="#94a3b8" />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#1e293b',
                      border: 'none',
                      borderRadius: '0.5rem',
                      color: '#f8fafc'
                    }}
                  />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="accuracy"
                    stroke={operationColors[operation as keyof typeof operationColors]}
                    name={language === 'en' ? 'Accuracy (%)' : 'Précision (%)'}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Progress;